import React, { useEffect, useState } from "react";

import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import LockIcon from "@mui/icons-material/Lock";

import request from "../../../Helpers/API";
import Swal from "sweetalert2";
import { TipoDomicilioArray } from "../../../Arrays/TipoDomicilioArray";
import { CRM_LeyendaAuditoria } from "./CRM_LeyendaAuditoria";
import { CRM_LeyendaDepartamento } from "./CRM_LeyendaDepartamento";

const DIRECCION_VACIA = {
  orden: -1,
  direccion: "",
  cp: "",
  idestado: "",
  estado: "",
  idmunicipio: "",
  municipio: "",
  idlocalidad: "",
  localidad: "",
  tipodomicilio: "",
  referencia1: "",
  referencia2: "",
  mapsUrl: "",
};

// Alta de cliente nuevo — inicia con una dirección en blanco lista para capturar
const FORM_VACIO = [{ ...DIRECCION_VACIA }];

// Convierte mdl_Info_Cliente_Ubicacion_CRM (carga inicial, GetInfoClienteID) al shape de esta pantalla
const mapUbicacionToForm = (u) => ({
  orden: u.orden || 0,
  direccion: u.direccion || "",
  cp: u.cp ? String(u.cp) : "",
  idestado: u.idestado || "",
  estado: u.estado || "",
  idmunicipio: u.idmunicipio || "",
  municipio: u.municipio || "",
  idlocalidad: u.idlocalidad || "",
  localidad: u.localidad || "",
  tipodomicilio: u.tipodomicilio || "",
  referencia1: u.referencia1 || "",
  referencia2: u.referencia2 || "",
  mapsUrl: u.ubicacion || "",
});

// Convierte mdlClientesDomicilioList (respuesta del guardado batch, ClientesDomicilio) al shape de esta pantalla
// Usa codigo_postal (no cp) y no trae ubicacion — se conserva el mapsUrl que ya estaba en memoria
const mapRespuestaGuardadoToForm = (u, mapsUrlPrevio) => ({
  orden: u.orden || 0,
  direccion: u.direccion || "",
  cp: u.cp ? String(u.cp) : "",
  idestado: u.idestado || "",
  estado: u.estado || "",
  idmunicipio: u.idmunicipio || "",
  municipio: u.municipio || "",
  idlocalidad: u.idlocalidad || "",
  localidad: u.localidad || "",
  tipodomicilio: u.tipodomicilio || u.idtipodomicilio || "",
  referencia1: u.referencia1 || "",
  referencia2: u.referencia2 || "",
  mapsUrl: mapsUrlPrevio || "",
});

// Convierte un enlace normal de Google Maps a su formato de embed público (sin API key)
// Usado solo por la previsualización del mapa — comentada por ahora
// const toEmbedUrl = (url) => {
//   if (!url) return "";
//   if (url.includes("output=embed")) return url;
//   const separator = url.includes("?") ? "&" : "?";
//   return `${url}${separator}output=embed`;
// };

export const CRM_DetalleUbicacion = ({ esNuevo, idcliente, ubicacionCliente = [], opcionesUbicacion = { estado: [], municipio: [] }, setLoader, soloLectura = false, responsableDepartamento = [] }) => {
  // info_ubicacion_cliente (GetInfoClienteID) trae activas e inactivas — solo se muestran las activas.
  // La dirección con orden 1 es la de Buró de crédito — se sigue mostrando aquí pero bloqueada
  // (sin edición, sin estrella, sin eliminar); solo se puede modificar desde esa sección
  const ubicacionesActivas = ubicacionCliente.filter((u) => u.estatus);

  const valorInicial = esNuevo || ubicacionesActivas.length === 0
    ? FORM_VACIO
    : ubicacionesActivas.map(mapUbicacionToForm);

  const [editing, setEditing] = useState(esNuevo);
  const [direcciones, setDirecciones] = useState(valorInicial);
  const [principalIdx, setPrincipalIdx] = useState(
    Math.max(0, ubicacionesActivas.findIndex((u) => u.principal))
  );
  // const [previewIdx, setPreviewIdx] = useState(0); // Previsualización del mapa — comentada por ahora
  const [localidadesPorFila, setLocalidadesPorFila] = useState({});
  const [buscandoPorFila, setBuscandoPorFila] = useState({});

  // ubicacionCliente llega después del primer render (fetch async en CRM_DetalleScreen) — se sincroniza aquí
  useEffect(() => {
    if (esNuevo || ubicacionesActivas.length === 0) return;
    setDirecciones(ubicacionesActivas.map(mapUbicacionToForm));
    setPrincipalIdx(Math.max(0, ubicacionesActivas.findIndex((u) => u.principal)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ubicacionCliente]);

  const handleChangeCampo = (idx, campo) => (e) => {
    setDirecciones((prev) =>
      prev.map((d, i) => (i === idx ? { ...d, [campo]: e.target.value } : d))
    );
  };

  const handleAgregarDireccion = () => {
    setDirecciones((prev) => [...prev, { ...DIRECCION_VACIA }]);
  };

  // const handlePrevisualizar = (idx) => setPreviewIdx(idx); // Previsualización del mapa — comentada por ahora

  const handleEditar = () => setEditing(true);
  const handleCancelar = () => {
    setDirecciones(valorInicial);
    setPrincipalIdx(Math.max(0, ubicacionesActivas.findIndex((u) => u.principal)));
    setLocalidadesPorFila({});
    setEditing(false);
  };
  // Valida y arma un elemento del array Domicilios para ClientesDomicilio (mdlClienteDomicilioArray)
  const validarYArmarDomicilio = (dir, idx, { estatus = true, principalOverride } = {}) => {
    if (!dir.cp || dir.cp.length !== 5) {
      Swal.fire("CLIENTES", `Dirección ${idx + 1}: el código postal es obligatorio y debe tener 5 dígitos`, "warning");
      return null;
    }
    if (!dir.idlocalidad) {
      Swal.fire("CLIENTES", `Dirección ${idx + 1}: el campo localidad es obligatorio`, "warning");
      return null;
    }
    if (!dir.tipodomicilio) {
      Swal.fire("CLIENTES", `Dirección ${idx + 1}: el campo tipo de domicilio es obligatorio`, "warning");
      return null;
    }
    if (!dir.direccion.trim()) {
      Swal.fire("CLIENTES", `Dirección ${idx + 1}: el campo dirección es obligatorio`, "warning");
      return null;
    }

    return {
      orden: Number(dir.orden ?? -1),
      idlocalidad: Number(dir.idlocalidad),
      direccion: dir.direccion,
      tipodomicilio: dir.tipodomicilio,
      principal: principalOverride ?? idx === principalIdx,
      referencia1: dir.referencia1,
      referencia2: dir.referencia2,
      estatus,
      ubicacion: dir.mapsUrl,
    };
  };

  // La fila de Buró de crédito (orden 1) es de solo lectura en esta sección — lo único
  // modificable aquí es marcarla como principal, así que se arma sin validar (sus datos ya
  // están guardados y no se tocan) y solo se recalcula el flag principal
  const armarDomicilioBuro = (dir, idx, { estatus = true, principalOverride } = {}) => ({
    orden: Number(dir.orden ?? -1),
    idlocalidad: Number(dir.idlocalidad),
    direccion: dir.direccion,
    tipodomicilio: dir.tipodomicilio,
    principal: principalOverride ?? idx === principalIdx,
    referencia1: dir.referencia1,
    referencia2: dir.referencia2,
    estatus,
    ubicacion: dir.mapsUrl,
  });

  // Guarda el array completo de direcciones en una sola consulta — el endpoint regresa
  // el listado ya actualizado (mdlClientesDomicilioList), que se carga directo al state.
  // La dirección de orden 1 (Buró de crédito) es de solo lectura salvo el flag "principal" —
  // se incluye igual en el array con sus datos intactos para que ese cambio se guarde
  const handleGuardar = async () => {
    const domicilios = [];
    for (let i = 0; i < direcciones.length; i++) {
      const esBuro = direcciones[i].orden === 1;
      const domicilio = esBuro
        ? armarDomicilioBuro(direcciones[i], i)
        : validarYArmarDomicilio(direcciones[i], i);
      if (!domicilio) return;
      domicilios.push(domicilio);
    }

    const body = { IdCliente: Number(idcliente), Domicilios: domicilios, buro_credito: false };

    setLoader?.((prev) => ({ ...prev, mostrar: true }));
    try {
      const result = await request.post("ClientesDomicilio", body);
      // El listado que regresa el SP puede incluir direcciones inactivas (ej. una eliminada
      // previamente que no viaja en este guardado) — se filtran aquí también
      const activos = (result?.domicilios || []).filter((u) => u.estatus);
      const nuevasDirecciones = activos.map((u) => mapRespuestaGuardadoToForm(u, direcciones.find((d) => d.orden === u.orden)?.mapsUrl));
      setDirecciones(nuevasDirecciones);
      setPrincipalIdx(Math.max(0, nuevasDirecciones.findIndex((d) => d.orden === activos.find((u) => u.principal)?.orden)));
      setLoader?.((prev) => ({ ...prev, mostrar: false }));
      await Swal.fire("CLIENTES", "Direcciones guardadas con éxito", "success");
      setEditing(false);
    } catch (error) {
      setLoader?.((prev) => ({ ...prev, mostrar: false }));
      Swal.fire("CLIENTES", JSON.stringify(error), "error");
    }
  };

  // Si tras eliminar no queda ninguna dirección, se deja una fila en blanco lista para
  // capturar (mismo estado inicial que un cliente sin domicilios) en vez de mostrar vacío
  const aplicarResultadoTrasEliminar = (nuevasDirecciones) => {
    if (nuevasDirecciones.length === 0) {
      setDirecciones([{ ...DIRECCION_VACIA }]);
      setPrincipalIdx(0);
      return;
    }
    setDirecciones(nuevasDirecciones);
  };

  // Deshabilita una ubicación existente (estatus: false) — envía el array completo con
  // esa dirección marcada como inactiva, mismo endpoint batch de guardado
  const handleDeshabilitar = (idx) => {
    const dir = direcciones[idx];
    if (!dir.orden || dir.orden <= 0) {
      // Dirección aún no guardada — se quita solo del state local
      const restantes = direcciones.filter((_, i) => i !== idx);
      aplicarResultadoTrasEliminar(restantes);
      if (restantes.length > 0 && principalIdx >= idx) setPrincipalIdx((p) => Math.max(0, p - 1));
      return;
    }

    Swal.fire({
      title: "¿Eliminar esta dirección?",
      text: "Esta dirección dejará de aparecer en el expediente del cliente.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed) return;

      const domicilios = direcciones.map((d, i) => {
        const esBuro = d.orden === 1;
        const principal = i === idx ? false : i === principalIdx;
        // La fila de Buró nunca se da de baja (no es eliminable desde aquí) — solo viaja
        // para reflejar un posible cambio de principal
        const estatus = esBuro ? true : i === idx ? false : true;
        return esBuro
          ? armarDomicilioBuro(d, i, { estatus, principalOverride: principal })
          : {
              orden: Number(d.orden ?? -1),
              idlocalidad: Number(d.idlocalidad),
              direccion: d.direccion,
              tipodomicilio: d.tipodomicilio,
              principal,
              referencia1: d.referencia1,
              referencia2: d.referencia2,
              estatus,
              ubicacion: d.mapsUrl,
            };
      });

      const body = { IdCliente: Number(idcliente), Domicilios: domicilios, buro_credito: false };

      setLoader?.((prev) => ({ ...prev, mostrar: true }));
      request
        .post("ClientesDomicilio", body)
        .then(async (result) => {
          const activos = (result?.domicilios || []).filter((u) => u.estatus);
          const nuevasDirecciones = activos.map((u) => mapRespuestaGuardadoToForm(u, direcciones.find((d) => d.orden === u.orden)?.mapsUrl));
          aplicarResultadoTrasEliminar(nuevasDirecciones);
          if (activos.length > 0) {
            setPrincipalIdx(Math.max(0, nuevasDirecciones.findIndex((d) => d.orden === activos.find((u) => u.principal)?.orden)));
          }
          setLoader?.((prev) => ({ ...prev, mostrar: false }));
          // Eliminar equivale a un guardado completo — se sale del modo edición, igual que handleGuardar
          setEditing(false);
          await Swal.fire("CLIENTES", "Dirección eliminada con éxito", "success");
        })
        .catch((error) => {
          setLoader?.((prev) => ({ ...prev, mostrar: false }));
          Swal.fire("CLIENTES", JSON.stringify(error), "error");
        });
    });
  };

  // Busca por código postal — bloquea Estado/Municipio y resuelve sus valores cruzando los catálogos ya cargados
  const buscarPorCP = (idx) => {
    const cp = direcciones[idx].cp;
    if (!cp) return;

    setBuscandoPorFila((prev) => ({ ...prev, [idx]: true }));
    request
      .get(`Clientes/GetLocalidadesCRM?codigo_postal=${cp}`)
      .then((result) => {
        const localidades = result || [];
        setLocalidadesPorFila((prev) => ({ ...prev, [idx]: localidades }));

        const idmunicipio = localidades[0]?.idmunicipio || "";
        const municipioEncontrado = opcionesUbicacion.municipio.find((m) => String(m.idmunicipio) === String(idmunicipio));
        const estadoEncontrado = municipioEncontrado
          ? opcionesUbicacion.estado.find((e) => String(e.idestado) === String(municipioEncontrado.idestado))
          : null;

        setDirecciones((prev) =>
          prev.map((d, i) => (i === idx
            ? {
                ...d,
                idmunicipio,
                municipio: municipioEncontrado?.municipio || "",
                idestado: estadoEncontrado?.idestado || "",
                estado: estadoEncontrado?.estado || "",
                idlocalidad: "",
                localidad: "",
              }
            : d))
        );
      })
      .catch((error) => Swal.fire("CLIENTES", JSON.stringify(error), "error"))
      .finally(() => setBuscandoPorFila((prev) => ({ ...prev, [idx]: false })));
  };

  // Se borra el CP — se desbloquean Estado/Municipio para elegirlos manualmente
  const handleLimpiarCP = (idx) => {
    setDirecciones((prev) =>
      prev.map((d, i) => (i === idx
        ? { ...d, cp: "", idestado: "", estado: "", idmunicipio: "", municipio: "", idlocalidad: "", localidad: "" }
        : d))
    );
    setLocalidadesPorFila((prev) => ({ ...prev, [idx]: [] }));
  };

  const handleChangeEstado = (idx) => (e) => {
    const idestado = e.target.value;
    const estadoEncontrado = opcionesUbicacion.estado.find((es) => String(es.idestado) === String(idestado));
    setDirecciones((prev) =>
      prev.map((d, i) => (i === idx
        ? { ...d, idestado, estado: estadoEncontrado?.estado || "", idmunicipio: "", municipio: "", idlocalidad: "", localidad: "" }
        : d))
    );
    setLocalidadesPorFila((prev) => ({ ...prev, [idx]: [] }));
  };

  // Al elegir municipio (solo posible sin CP) se cargan sus localidades
  const handleChangeMunicipio = (idx) => (e) => {
    const idmunicipio = e.target.value;
    const municipioEncontrado = opcionesUbicacion.municipio.find((m) => String(m.idmunicipio) === String(idmunicipio));
    setDirecciones((prev) =>
      prev.map((d, i) => (i === idx
        ? { ...d, idmunicipio, municipio: municipioEncontrado?.municipio || "", idlocalidad: "", localidad: "" }
        : d))
    );

    if (!idmunicipio) {
      setLocalidadesPorFila((prev) => ({ ...prev, [idx]: [] }));
      return;
    }

    setBuscandoPorFila((prev) => ({ ...prev, [idx]: true }));
    request
      .get(`Clientes/GetLocalidadesCRM?idmunicipio=${idmunicipio}`)
      .then((result) => setLocalidadesPorFila((prev) => ({ ...prev, [idx]: result || [] })))
      .catch((error) => Swal.fire("CLIENTES", JSON.stringify(error), "error"))
      .finally(() => setBuscandoPorFila((prev) => ({ ...prev, [idx]: false })));
  };

  // Al elegir localidad se autocompleta su código postal
  const handleChangeLocalidad = (idx) => (e) => {
    const idlocalidad = e.target.value;
    const localidades = localidadesPorFila[idx] || [];
    const encontrada = localidades.find((l) => String(l.idlocalidad) === String(idlocalidad));
    setDirecciones((prev) =>
      prev.map((d, i) => (i === idx
        ? { ...d, idlocalidad, localidad: encontrada?.localidad || "", cp: encontrada?.cp ? String(encontrada.cp) : d.cp }
        : d))
    );
  };

  return (
    <div id="sec-ubicacion" className="crm-detalle-card" style={{ scrollMarginTop: 170 }}>
      <div className="crm-detalle-card-header crm-detalle-card-header-actions">
        <div>
          <h3>Ubicación</h3>
          <p>Direcciones registradas del cliente</p>
        </div>
        <div className="crm-detalle-card-header-buttons">
          {editing ? (
            <>
              <button type="button" className="btn-icono-ghost" title="Cancelar" onClick={handleCancelar}>
                <CloseIcon />
              </button>
              <button type="button" className="btn-icono-solido" title="Guardar cambios" onClick={handleGuardar}>
                <CheckIcon />
                <span>Guardar</span>
              </button>
            </>
          ) : (
            !soloLectura && (
            <button type="button" className="btn-icono-ghost btn-editar" title="Editar" onClick={handleEditar}>
              <EditIcon />
              <span>Editar</span>
            </button>
            )
          )}
        </div>
      </div>

      <div className="crm-detalle-card-body">
        {!esNuevo && !editing && direcciones.length === 0 && (
          <p className="crm-sin-info">Este cliente no tiene domicilios guardados</p>
        )}

        {direcciones.map((dir, idx) => {
          const esPrincipal = idx === principalIdx;
          const esBuro = dir.orden === 1;
          const editable = editing && !esBuro;
          const cpBloqueado = !!dir.cp;
          const buscando = !!buscandoPorFila[idx];

          // Si aún no se ha buscado el catálogo de localidades para esta fila pero la
          // dirección ya trae una localidad guardada, se agrega como opción de respaldo
          // para que el select la muestre seleccionada en vez de "Seleccionar"
          const localidades = localidadesPorFila[idx] || (
            dir.idlocalidad ? [{ idlocalidad: dir.idlocalidad, localidad: dir.localidad }] : []
          );
          const campoClass = () => (editable ? "crm-field-editable" : "crm-field-readonly");

          return (
            <div key={idx} className={`crm-detalle-direccion-card${esPrincipal ? " direccion-principal" : ""}`}>
              <div className="crm-detalle-direccion-header">
                <span className="direccion-titulo">{esBuro ? "Buró de crédito" : `Dirección ${idx + 1}`}</span>
                {esPrincipal && <span className="direccion-badge-principal">Principal</span>}
                <button
                  type="button"
                  className="direccion-star"
                  disabled={!editing}
                  onClick={() => editing && setPrincipalIdx(idx)}
                >
                  {esPrincipal ? <StarIcon /> : <StarBorderIcon />}
                </button>
                {esBuro ? (
                  <span className="direccion-badge-bloqueada" title="El resto de los datos solo se modifica desde la sección Buró de crédito">
                    <LockIcon fontSize="small" /> Bloqueada
                  </span>
                ) : (
                  <button
                    type="button"
                    className="direccion-cancelar"
                    title="Eliminar dirección"
                    disabled={!editing}
                    onClick={() => editing && handleDeshabilitar(idx)}
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                )}
              </div>

              <div className="crm-detalle-direccion-body">
                <div className="crm-field">
                  <label>Dirección</label>
                  <input type="text" className={campoClass()} disabled={!editable} value={dir.direccion} onChange={handleChangeCampo(idx, "direccion")} />
                </div>

                <div className="crm-detalle-grid crm-detalle-grid-4">
                  <div className="crm-field">
                    <label>Código postal</label>
                    <div className="crm-field-inline-btn">
                      <div className="crm-field-cp-wrap">
                        <input
                          type="text"
                          className={`mono ${campoClass()}`}
                          disabled={!editable}
                          value={dir.cp}
                          onChange={handleChangeCampo(idx, "cp")}
                          onKeyDown={(e) => e.key === "Enter" && buscarPorCP(idx)}
                        />
                        {editable && dir.cp && (
                          <button type="button" className="btn-cp-limpiar" title="Quitar CP" onClick={() => handleLimpiarCP(idx)}>×</button>
                        )}
                      </div>
                      {editable && (
                        <button type="button" className="btn-cp-buscar" title="Buscar por CP" disabled={!dir.cp || buscando} onClick={() => buscarPorCP(idx)}>
                          <SearchIcon fontSize="small" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="crm-field">
                    <label>Estado</label>
                    <select className={campoClass()} disabled={!editable || cpBloqueado} value={dir.idestado} onChange={handleChangeEstado(idx)}>
                      <option value="">Seleccionar</option>
                      {opcionesUbicacion.estado.map((es) => (
                        <option key={es.idestado} value={es.idestado}>{es.estado}</option>
                      ))}
                    </select>
                  </div>
                  <div className="crm-field">
                    <label>Municipio</label>
                    <select className={campoClass()} disabled={!editable || cpBloqueado || !dir.idestado} value={dir.idmunicipio} onChange={handleChangeMunicipio(idx)}>
                      <option value="">Seleccionar</option>
                      {opcionesUbicacion.municipio
                        .filter((m) => String(m.idestado) === String(dir.idestado))
                        .map((m) => (
                          <option key={m.idmunicipio} value={m.idmunicipio}>{m.municipio}</option>
                        ))}
                    </select>
                  </div>
                  <div className="crm-field">
                    <label>Localidad</label>
                    <select className={campoClass()} disabled={!editable || localidades.length === 0} value={dir.idlocalidad} onChange={handleChangeLocalidad(idx)}>
                      <option value="">Seleccionar</option>
                      {localidades.map((l) => (
                        <option key={l.idlocalidad} value={l.idlocalidad}>{l.localidad}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="crm-detalle-grid crm-detalle-grid-4">
                  <div className="crm-field">
                    <label>Tipo de domicilio</label>
                    <select className={campoClass()} disabled={!editable} value={dir.tipodomicilio} onChange={handleChangeCampo(idx, "tipodomicilio")}>
                      <option value="">Seleccionar</option>
                      {TipoDomicilioArray.map((t) => (
                        <option key={t.id} value={t.id}>{t.display}</option>
                      ))}
                    </select>
                  </div>
                  <div className="crm-field">
                    <label>Referencia 1</label>
                    <input type="text" className={campoClass()} disabled={!editable} value={dir.referencia1} onChange={handleChangeCampo(idx, "referencia1")} />
                  </div>
                  <div className="crm-field">
                    <label>Referencia 2</label>
                    <input type="text" className={campoClass()} disabled={!editable} value={dir.referencia2} onChange={handleChangeCampo(idx, "referencia2")} />
                  </div>
                  <div className="crm-field">
                    <label>Enlace de Google Maps</label>
                    <input type="text" className={campoClass()} disabled={!editable} value={dir.mapsUrl} onChange={handleChangeCampo(idx, "mapsUrl")} />
                  </div>
                </div>

                {/* Previsualización del mapa — comentada por ahora
                <div className="crm-detalle-maps-row">
                  <button type="button" className="btn-previsualizar" disabled={!dir.mapsUrl} onClick={() => handlePrevisualizar(idx)}>
                    Previsualizar
                  </button>
                </div>

                {idx === previewIdx && dir.mapsUrl && (
                  <div className="crm-detalle-mapa-preview">
                    <iframe
                      className="mapa-iframe"
                      title={`Mapa dirección ${idx + 1}`}
                      src={toEmbedUrl(dir.mapsUrl)}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                    <div className="mapa-info">
                      <span className="mapa-coords mono">{dir.coords}</span>
                      <a href={dir.mapsUrl} target="_blank" rel="noreferrer" className="mapa-link">Abrir en Maps</a>
                    </div>
                  </div>
                )}
                */}
              </div>
            </div>
          );
        })}

        {editing && (
          <button type="button" className="chip-add-pill" onClick={handleAgregarDireccion}>
            + Agregar dirección
          </button>
        )}
      </div>

      <div className="crm-detalle-card-footer">
        <CRM_LeyendaDepartamento responsables={responsableDepartamento} idrel={3} />
        <CRM_LeyendaAuditoria auditoria={ubicacionCliente[0]} />
      </div>
    </div>
  );
};
